import React, { useState, useEffect } from 'react';
import { 
  Home,
  MapPin,
  Phone,
  Mail,
  Clock,
  DollarSign,
  Calendar,
  User,
  Camera,
  FileText,
  Star,
  Award,
  Hammer,
  CheckCircle,
  AlertTriangle,
  Filter,
  Search,
  Video,
  MessageSquare,
  Download,
  Upload,
  Eye,
  PhoneCall,
  Navigation,
  Image as ImageIcon
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedBadge,
  BrandedInput,
  BrandedSelect
} from '../../components/branding/BrandedComponents';

interface ContractorDashboardProps {
  contractorName?: string;
  companyName?: string;
  contractorId?: string;
}

interface Project {
  projectId: string;
  customerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  category: string;
  urgency: 'emergency' | 'urgent' | 'soon' | 'planning';
  budget: string;
  title: string;
  description: string;
  preferredContactTime: string;
  status: 'new' | 'waiting' | 'reviewing' | 'assigned' | 'ready' | 'in_progress' | 'completed' | 'rejected';
  createdAt: string;
  photos?: string[];
  estimatedValue?: number;
  distance?: number;
  priority?: number;
}

export const ContractorDashboard: React.FC<ContractorDashboardProps> = ({
  contractorName = 'Mike Johnson',
  companyName = 'HomeExpert Contractors',
  contractorId = 'contractor_001'
}) => {
  return (
    <BrandingProvider templateType="business" initialBranding={{ companyName }}>
      <ContractorDashboardContent 
        contractorName={contractorName}
        companyName={companyName}
        contractorId={contractorId}
      />
    </BrandingProvider>
  );
};

const ContractorDashboardContent: React.FC<ContractorDashboardProps> = ({
  contractorName = 'Mike Johnson',
  companyName = 'HomeExpert Contractors',
  contractorId = 'contractor_001'
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [stats, setStats] = useState({
    totalProjects: 0,
    newProjects: 0,
    inProgress: 0,
    completed: 0,
    totalValue: 0,
    avgRating: 4.8
  });

  // Fetch real projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/homeimprovement/projects?status=waiting', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.projects) {
            setProjects(data.projects);
            setFilteredProjects(data.projects);
            
            // Calculate stats from real data
            setStats({
              totalProjects: data.projects.length,
              newProjects: data.projects.filter((p: Project) => p.status === 'new').length,
              inProgress: data.projects.filter((p: Project) => p.status === 'in_progress').length,
              completed: data.projects.filter((p: Project) => p.status === 'completed').length,
              totalValue: data.projects.reduce((sum: number, p: Project) => sum + (p.estimatedValue || 0), 0),
              avgRating: 4.8
            });
          } else {
            // No projects found
            setProjects([]);
            setFilteredProjects([]);
            setStats({
              totalProjects: 0,
              newProjects: 0,
              inProgress: 0,
              completed: 0,
              totalValue: 0,
              avgRating: 4.8
            });
          }
        } else {
          console.error('Failed to fetch projects');
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
    
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(fetchProjects, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter projects
  useEffect(() => {
    let filtered = projects;

    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(project => project.category === filterCategory);
    }

    if (filterUrgency !== 'all') {
      filtered = filtered.filter(project => project.urgency === filterUrgency);
    }

    // Sort by priority and urgency
    filtered.sort((a, b) => {
      const urgencyOrder = { emergency: 4, urgent: 3, soon: 2, planning: 1 };
      const urgencyDiff = urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
      if (urgencyDiff !== 0) return urgencyDiff;
      return (b.priority || 0) - (a.priority || 0);
    });

    setFilteredProjects(filtered);
  }, [projects, searchTerm, filterCategory, filterUrgency]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'kitchen': return '🍳';
      case 'bathroom': return '🛁';
      case 'roofing': return '🏠';
      case 'flooring': return '🪵';
      case 'painting': return '🎨';
      case 'electrical': return '⚡';
      case 'plumbing': return '🔧';
      case 'hvac': return '❄️';
      case 'landscaping': return '🌳';
      default: return '🔨';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'emergency': return 'error';
      case 'urgent': return 'warning';
      case 'soon': return 'primary';
      case 'planning': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'primary';
      case 'assigned': return 'warning';
      case 'ready': return 'success';
      case 'in_progress': return 'accent';
      case 'completed': return 'success';
      default: return 'secondary';
    }
  };

  const handleAcceptProject = async (project: Project) => {
    try {
      // Accept project and create meeting room
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/homeimprovement/projects/${project.projectId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractorId,
          contractorName
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Debug logging to identify the issue - also store in localStorage to persist across redirect
        const debugInfo = {
          acceptResponse: result,
          projectData: project,
          meetingId: result.meetingId,
          projectId: project.projectId,
          timestamp: new Date().toISOString()
        };
        console.log('🎯 [ContractorDashboard] Accept project response:', result);
        console.log('🎯 [ContractorDashboard] Project data:', project);
        console.log('🎯 [ContractorDashboard] Meeting ID:', result.meetingId);
        console.log('🎯 [ContractorDashboard] Project ID:', project.projectId);
        
        // Store debug info in localStorage to check after redirect
        localStorage.setItem('homeimprovement_debug', JSON.stringify(debugInfo));
        
        // Validate we have required data before redirecting
        if (!result.meetingId) {
          console.error('🚨 [ContractorDashboard] Missing meetingId in response!', result);
          alert('Error: No meeting ID received from server. Please try again.');
          return;
        }
        if (!project.projectId) {
          console.error('🚨 [ContractorDashboard] Missing projectId in project data!', project);
          alert('Error: No project ID found. Please refresh and try again.');
          return;
        }
        
        // Remove project from waiting list since it's been accepted
        setProjects(prev => prev.filter(p => p.projectId !== project.projectId));

        // Extract org ID from current URL to preserve organization context
        const pathname = window.location.pathname;
        const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
        const orgPrefix = orgMatch ? `/${orgMatch[1]}` : '';
        
        // Redirect to video consultation with proper org prefix
        const contractorJoinUrl = `${orgPrefix}/templates/homeimprovement/session/contractor?roomId=${result.meetingId}&projectId=${project.projectId}&contractorName=${encodeURIComponent(contractorName)}&customerName=${encodeURIComponent(project.customerName)}&projectTitle=${encodeURIComponent(project.title)}&category=${project.category}&urgency=${project.urgency}`;
        
        console.log('🎯 [ContractorDashboard] Org prefix:', orgPrefix);
        console.log('🎯 [ContractorDashboard] Redirecting to URL:', contractorJoinUrl);
        localStorage.setItem('homeimprovement_redirect_url', contractorJoinUrl);
        window.location.href = contractorJoinUrl;
      }
    } catch (error) {
      console.error('Error accepting project:', error);
    }
  };

  const handleRejectProject = async (project: Project) => {
    try {
      // Reject project and update status to rejected
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/homeimprovement/projects/${project.projectId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contractorId,
          contractorName
        })
      });

      if (response.ok) {
        // Remove project from waiting list since it's been rejected
        setProjects(prev => prev.filter(p => p.projectId !== project.projectId));
      }
    } catch (error) {
      console.error('Error rejecting project:', error);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background)' }}>
      {/* Header */}
      <header className="border-b" style={{ 
        backgroundColor: 'var(--brand-surface)', 
        borderColor: 'var(--brand-border)' 
      }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <BrandedHeader showLogo={true} showTagline={false} />
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
                  {contractorName}
                </p>
                <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                  Licensed Contractor
                </p>
              </div>
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={16} 
                    className={i < Math.floor(stats.avgRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'} 
                  />
                ))}
                <span className="text-sm ml-1" style={{ color: 'var(--brand-text-secondary)' }}>
                  {stats.avgRating}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <BrandedCard className="text-center">
            <div className="p-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--brand-primary)', opacity: 0.1 }}>
                <Home size={24} style={{ color: 'var(--brand-primary)' }} />
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--brand-text-primary)' }}>
                {stats.totalProjects}
              </p>
              <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                Total Projects
              </p>
            </div>
          </BrandedCard>

          <BrandedCard className="text-center">
            <div className="p-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--brand-warning)', opacity: 0.1 }}>
                <AlertTriangle size={24} style={{ color: 'var(--brand-warning)' }} />
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--brand-text-primary)' }}>
                {stats.newProjects}
              </p>
              <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                New Projects
              </p>
            </div>
          </BrandedCard>

          <BrandedCard className="text-center">
            <div className="p-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--brand-success)', opacity: 0.1 }}>
                <DollarSign size={24} style={{ color: 'var(--brand-success)' }} />
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--brand-text-primary)' }}>
                ${(stats.totalValue / 1000).toFixed(0)}K
              </p>
              <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                Pipeline Value
              </p>
            </div>
          </BrandedCard>

          <BrandedCard className="text-center">
            <div className="p-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--brand-accent)', opacity: 0.1 }}>
                <Award size={24} style={{ color: 'var(--brand-accent)' }} />
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--brand-text-primary)' }}>
                {stats.avgRating}
              </p>
              <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                Avg Rating
              </p>
            </div>
          </BrandedCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Project List */}
          <div className="lg:col-span-2">
            <BrandedCard>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold flex items-center" style={{ color: 'var(--brand-text-primary)' }}>
                    <Hammer size={24} className="mr-3" />
                    Available Projects
                  </h2>
                  <BrandedBadge variant="primary">
                    {filteredProjects.length} Projects
                  </BrandedBadge>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <BrandedInput
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                  <BrandedSelect
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    options={[
                      { value: 'all', label: 'All Categories' },
                      { value: 'kitchen', label: '🍳 Kitchen' },
                      { value: 'bathroom', label: '🛁 Bathroom' },
                      { value: 'roofing', label: '🏠 Roofing' },
                      { value: 'flooring', label: '🪵 Flooring' },
                      { value: 'painting', label: '🎨 Painting' },
                      { value: 'electrical', label: '⚡ Electrical' },
                      { value: 'plumbing', label: '🔧 Plumbing' }
                    ]}
                  />
                  <BrandedSelect
                    value={filterUrgency}
                    onChange={(e) => setFilterUrgency(e.target.value)}
                    options={[
                      { value: 'all', label: 'All Urgency' },
                      { value: 'emergency', label: '🚨 Emergency' },
                      { value: 'urgent', label: '⏰ Urgent' },
                      { value: 'soon', label: '📅 Soon' },
                      { value: 'planning', label: '💭 Planning' }
                    ]}
                  />
                </div>

                {/* Project List */}
                <div className="space-y-4">
                  {filteredProjects.map((project) => (
                    <div 
                      key={project.projectId}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      style={{ borderColor: 'var(--brand-border)' }}
                      onClick={() => setSelectedProject(project)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl">{getCategoryIcon(project.category)}</span>
                          <div className="flex-1">
                            <h3 className="font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
                              {project.title}
                            </h3>
                            <p className="text-sm flex items-center mt-1" style={{ color: 'var(--brand-text-secondary)' }}>
                              <User size={14} className="mr-1" />
                              {project.customerName}
                            </p>
                            <p className="text-sm flex items-center" style={{ color: 'var(--brand-text-secondary)' }}>
                              <MapPin size={14} className="mr-1" />
                              {project.address}, {project.city} ({project.distance}mi away)
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <BrandedBadge variant={getUrgencyColor(project.urgency)} size="small" className="mb-2">
                            {project.urgency.toUpperCase()}
                          </BrandedBadge>
                          <p className="text-sm font-medium" style={{ color: 'var(--brand-success)' }}>
                            ${project.estimatedValue?.toLocaleString()}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--brand-text-muted)' }}>
                            {formatTimeAgo(project.createdAt)}
                          </p>
                        </div>
                      </div>

                      <p className="text-sm mb-3" style={{ color: 'var(--brand-text-secondary)' }}>
                        {project.description.substring(0, 150)}...
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {project.photos && project.photos.length > 0 && (
                            <div className="flex items-center text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                              <ImageIcon size={14} className="mr-1" />
                              {project.photos.length} photos
                            </div>
                          )}
                          <div className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                            Budget: {project.budget.replace('-', ' - $').replace('k', ',000')}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <BrandedButton
                            size="small"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProject(project);
                            }}
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </BrandedButton>
                          {project.status === 'waiting' ? (
                            <>
                              <BrandedButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAcceptProject(project);
                                }}
                              >
                                <Video size={14} className="mr-1" />
                                Accept
                              </BrandedButton>
                              <BrandedButton
                                size="small"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRejectProject(project);
                                }}
                              >
                                Reject
                              </BrandedButton>
                            </>
                          ) : (
                            <BrandedButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAcceptProject(project);
                              }}
                            >
                              <Video size={14} className="mr-1" />
                              Start Session
                            </BrandedButton>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </BrandedCard>
          </div>

          {/* Project Details Panel */}
          <div className="lg:col-span-1">
            {selectedProject ? (
              <BrandedCard>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
                      Project Details
                    </h3>
                    <BrandedBadge variant={getUrgencyColor(selectedProject.urgency)}>
                      {selectedProject.urgency.toUpperCase()}
                    </BrandedBadge>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                        {selectedProject.title}
                      </h4>
                      <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                        {selectedProject.description}
                      </p>
                    </div>

                    <div className="border-t pt-4" style={{ borderColor: 'var(--brand-border)' }}>
                      <h4 className="font-medium mb-3" style={{ color: 'var(--brand-text-primary)' }}>
                        Customer Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <User size={14} className="mr-2" />
                          <span>{selectedProject.customerName}</span>
                        </div>
                        <div className="flex items-center">
                          <Mail size={14} className="mr-2" />
                          <span>{selectedProject.email}</span>
                        </div>
                        <div className="flex items-center">
                          <Phone size={14} className="mr-2" />
                          <span>{selectedProject.phone}</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin size={14} className="mr-2" />
                          <span>{selectedProject.address}, {selectedProject.city}, {selectedProject.state} {selectedProject.zipCode}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4" style={{ borderColor: 'var(--brand-border)' }}>
                      <h4 className="font-medium mb-3" style={{ color: 'var(--brand-text-primary)' }}>
                        Project Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--brand-text-secondary)' }}>Category:</span>
                          <span>{getCategoryIcon(selectedProject.category)} {selectedProject.category}</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--brand-text-secondary)' }}>Budget:</span>
                          <span>{selectedProject.budget.replace('-', ' - $').replace('k', ',000')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--brand-text-secondary)' }}>Est. Value:</span>
                          <span className="font-medium" style={{ color: 'var(--brand-success)' }}>
                            ${selectedProject.estimatedValue?.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--brand-text-secondary)' }}>Distance:</span>
                          <span>{selectedProject.distance} miles</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--brand-text-secondary)' }}>Preferred Contact:</span>
                          <span>{selectedProject.preferredContactTime}</span>
                        </div>
                      </div>
                    </div>

                    {selectedProject.photos && selectedProject.photos.length > 0 && (
                      <div className="border-t pt-4" style={{ borderColor: 'var(--brand-border)' }}>
                        <h4 className="font-medium mb-3" style={{ color: 'var(--brand-text-primary)' }}>
                          Project Photos ({selectedProject.photos.length})
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {selectedProject.photos.map((photo, index) => (
                            <div key={index} className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                              <ImageIcon size={24} style={{ color: 'var(--brand-text-muted)' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="border-t pt-4 space-y-3" style={{ borderColor: 'var(--brand-border)' }}>
                      {selectedProject.status === 'waiting' ? (
                        <div className="grid grid-cols-2 gap-3">
                          <BrandedButton 
                            className="w-full"
                            onClick={() => handleAcceptProject(selectedProject)}
                          >
                            <Video size={16} className="mr-2" />
                            Accept Project
                          </BrandedButton>
                          <BrandedButton 
                            className="w-full"
                            variant="outline"
                            onClick={() => handleRejectProject(selectedProject)}
                          >
                            Reject
                          </BrandedButton>
                        </div>
                      ) : (
                        <BrandedButton 
                          className="w-full"
                          onClick={() => handleAcceptProject(selectedProject)}
                        >
                          <Video size={16} className="mr-2" />
                          Start Video Consultation
                        </BrandedButton>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <BrandedButton variant="outline" size="small">
                          <PhoneCall size={14} className="mr-1" />
                          Call
                        </BrandedButton>
                        <BrandedButton variant="outline" size="small">
                          <Navigation size={14} className="mr-1" />
                          Directions
                        </BrandedButton>
                      </div>
                    </div>
                  </div>
                </div>
              </BrandedCard>
            ) : (
              <BrandedCard>
                <div className="p-6 text-center">
                  <Home size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-text-muted)' }} />
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                    Select a Project
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                    Click on a project from the list to view details and start a consultation.
                  </p>
                </div>
              </BrandedCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};